import * as R from 'ramda';
import React from 'react';
import styled from 'styled-components';

import { StandardButton } from '@ui/buttons/Button.tsx';
import { Icon } from '@ui/icons/Icon.tsx';
import { formatDirectionsDistance } from '@utils/distance.ts';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';
import { handleKeyboardSelect } from '../../../../utils/keyboardUtils.ts';
import MultiStopStepList from './multiStopStepList.tsx';
import {
  RouteInfo,
  ButtonsContainer,
  StepIconWrapper,
  EndNavigationButton,
  guidanceButtonTypography,
} from './styles.ts';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

const MultiStopListStyled = styled(MultiStopStepList)`
  overflow-y: hidden;
  width: 100%;
`;

const PrimaryButton = styled(StandardButton)`
  ${guidanceButtonTypography}
  width: 138px;
  height: 28.77px;
`;

const StyledButtonsContainer = styled(ButtonsContainer)`
  padding-bottom: 10px;
  flex-wrap: nowrap;
  justify-content: center;
`;

export const DirectionsResultViewImpl = ({
  className,
  endpoints,
  times,
  distances,
  searchInputs,
  steps,
  currentStep,
  currentStop,
  onPrevStepClick,
  onNextStepClick,
  onEndNavigationClick,
  isNavigating,
  onStepClicked,
  preferredUnits,
  T,
  isDesktop,
  locale,
}) => {
  const colors = useCSSColors();
  // Guard against empty endpoints during reset
  if (!endpoints || endpoints.length === 0) {
    return null;
  }

  const isLastStep = currentStop === steps.length - 1 && currentStep === steps.at(-1).length - 1;
  const showCloseButton = isNavigating && !!onEndNavigationClick && isLastStep;
  const onNextOrCloseClick = showCloseButton ? onEndNavigationClick : onNextStepClick;
  const distanceDisplay = formatDirectionsDistance(R.sum(distances), preferredUnits, T);
  const minutesText = R.sum(times)
    ? T('getDirectionsFromTo:_minutes_ min total', { minutes: R.sum(times) })
    : T('getDirectionsFromTo:<1 min');

  return (
    <Wrapper className={className} data-testid="DirectionsResultView">
      {isNavigating && (
        <RouteInfo data-testid="RouteInfo">
          <StepIconWrapper>
            <Icon name={'wayfinding.end'} cdn width={32} height={45} />
          </StepIconWrapper>
          <div>
            <span className="info" data-testid="InfoPrimary">
              {minutesText}
              {' • '}
              {distanceDisplay}
            </span>
            <span
              className="label"
              data-testid="InfoSecondary"
            >{`${T(`getDirectionsFromTo:To`)} ${endpoints[endpoints.length - 1].title}`}</span>
          </div>
        </RouteInfo>
      )}

      <StyledButtonsContainer>
        <PrimaryButton
          onClick={onPrevStepClick}
          disabled={currentStop === 0 && currentStep === 0}
          data-testid="PrevStepButton"
          aria-label={T('getDirectionsFromTo:Previous step')}
          aria-controls="Stepslist"
          onKeyDown={handleKeyboardSelect(() => onPrevStepClick())}
        >
          <Icon className="icon" width={24} height={24} name={'chevron-left'} cdn fillColor={colors.primary} />
          {T('getDirectionsFromTo:Prev')}
        </PrimaryButton>
        <PrimaryButton
          type="button"
          onClick={onNextOrCloseClick}
          disabled={isLastStep && !showCloseButton}
          data-testid={showCloseButton ? 'CloseNavigationButton' : 'NextStepButton'}
          aria-label={showCloseButton ? T('ui:Close') : T('getDirectionsFromTo:Next Step')}
          aria-controls={showCloseButton ? undefined : 'Stepslist'}
          onKeyDown={handleKeyboardSelect(() => onNextOrCloseClick())}
        >
          {showCloseButton ? T('ui:Close') : T('getDirectionsFromTo:Next')}
          {!showCloseButton && (
            <Icon className="icon" width={24} height={24} name={'chevron-right'} cdn fillColor={colors.primary} />
          )}
        </PrimaryButton>
      </StyledButtonsContainer>
      {isNavigating && onEndNavigationClick && !isLastStep && (
        <EndNavigationButton type="button" onClick={onEndNavigationClick}>
          {T('getDirectionsFromTo:End Navigation')}
        </EndNavigationButton>
      )}
      <MultiStopListStyled
        legs={steps}
        times={times}
        distances={distances}
        preferredUnits={preferredUnits}
        searchInputs={searchInputs}
        onStepClicked={onStepClicked}
        T={T}
        isNavigating={isNavigating}
        currentStop={currentStop}
        currentStep={currentStep}
        isDesktop={isDesktop}
        locale={locale}
      />
    </Wrapper>
  );
};

export default DirectionsResultViewImpl;
